import useLocale from "hooks/useLocale";
import type { GetStaticPaths, GetStaticProps } from "next";
import { useRouter } from "next/router";
import { DefaultMDXProvider } from "../../components/default-mdx-provider";
import * as AccessAr from "../../content/ar/articles/access.mdx";
import * as EquityAr from "../../content/ar/articles/equity.mdx";
import * as FinanceAr from "../../content/ar/articles/finance.mdx";
import * as FinanceAidAr from "../../content/ar/articles/finance-aid.mdx";
import * as LearningAr from "../../content/ar/articles/learning.mdx";
import * as QualityAr from "../../content/ar/articles/quality.mdx";
import * as TrajectoriesAr from "../../content/ar/articles/trajectories.mdx";
import * as AccessDe from "../../content/de/articles/access.mdx";
import * as EquityDe from "../../content/de/articles/equity.mdx";
import * as FinanceDe from "../../content/de/articles/finance.mdx";
import * as FinanceAidDe from "../../content/de/articles/finance-aid.mdx";
import * as LearningDe from "../../content/de/articles/learning.mdx";
import * as QualityDe from "../../content/de/articles/quality.mdx";
import * as TrajectoriesDe from "../../content/de/articles/trajectories.mdx";
import * as AccessEn from "../../content/en/articles/access.mdx";
import * as EquityEn from "../../content/en/articles/equity.mdx";
import * as FinanceEn from "../../content/en/articles/finance.mdx";
import * as FinanceAidEn from "../../content/en/articles/finance-aid.mdx";
import * as LearningEn from "../../content/en/articles/learning.mdx";
import * as QualityEn from "../../content/en/articles/quality.mdx";
import * as TrajectoriesEn from "../../content/en/articles/trajectories.mdx";
import * as AccessEs from "../../content/es/articles/access.mdx";
import * as EquityEs from "../../content/es/articles/equity.mdx";
import * as FinanceEs from "../../content/es/articles/finance.mdx";
import * as FinanceAidEs from "../../content/es/articles/finance-aid.mdx";
import * as LearningEs from "../../content/es/articles/learning.mdx";
import * as QualityEs from "../../content/es/articles/quality.mdx";
import * as TrajectoriesEs from "../../content/es/articles/trajectories.mdx";
import * as AccessFr from "../../content/fr/articles/access.mdx";
import * as EquityFr from "../../content/fr/articles/equity.mdx";
import * as FinanceFr from "../../content/fr/articles/finance.mdx";
import * as FinanceAidFr from "../../content/fr/articles/finance-aid.mdx";
import * as LearningFr from "../../content/fr/articles/learning.mdx";
import * as QualityFr from "../../content/fr/articles/quality.mdx";
import * as TrajectoriesFr from "../../content/fr/articles/trajectories.mdx";
import * as AccessRu from "../../content/ru/articles/access.mdx";
import * as EquityRu from "../../content/ru/articles/equity.mdx";
import * as FinanceRu from "../../content/ru/articles/finance.mdx";
import * as FinanceAidRu from "../../content/ru/articles/finance-aid.mdx";
import * as LearningRu from "../../content/ru/articles/learning.mdx";
import * as QualityRu from "../../content/ru/articles/quality.mdx";
import * as TrajectoriesRu from "../../content/ru/articles/trajectories.mdx";
import * as AccessZh from "../../content/zh/articles/access.mdx";
import * as EquityZh from "../../content/zh/articles/equity.mdx";
import * as FinanceZh from "../../content/zh/articles/finance.mdx";
import * as FinanceAidZh from "../../content/zh/articles/finance-aid.mdx";
import * as LearningZh from "../../content/zh/articles/learning.mdx";
import * as QualityZh from "../../content/zh/articles/quality.mdx";
import * as TrajectoriesZh from "../../content/zh/articles/trajectories.mdx";

const localeContents: Record<string, Record<string, MDXModule>> = {
  ar: {
    access: AccessAr,
    equity: EquityAr,
    learning: LearningAr,
    trajectories: TrajectoriesAr,
    quality: QualityAr,
    finance: FinanceAr,
    "finance-aid": FinanceAidAr,
  },
  de: {
    access: AccessDe,
    equity: EquityDe,
    learning: LearningDe,
    trajectories: TrajectoriesDe,
    quality: QualityDe,
    finance: FinanceDe,
    "finance-aid": FinanceAidDe,
  },
  en: {
    access: AccessEn,
    equity: EquityEn,
    learning: LearningEn,
    trajectories: TrajectoriesEn,
    quality: QualityEn,
    finance: FinanceEn,
    "finance-aid": FinanceAidEn,
  },
  es: {
    access: AccessEs,
    equity: EquityEs,
    learning: LearningEs,
    trajectories: TrajectoriesEs,
    quality: QualityEs,
    finance: FinanceEs,
    "finance-aid": FinanceAidEs,
  },
  fr: {
    access: AccessFr,
    equity: EquityFr,
    learning: LearningFr,
    trajectories: TrajectoriesFr,
    quality: QualityFr,
    finance: FinanceFr,
    "finance-aid": FinanceAidFr,
  },
  ru: {
    access: AccessRu,
    equity: EquityRu,
    learning: LearningRu,
    trajectories: TrajectoriesRu,
    quality: QualityRu,
    finance: FinanceRu,
    "finance-aid": FinanceAidRu,
  },
  zh: {
    access: AccessZh,
    equity: EquityZh,
    learning: LearningZh,
    trajectories: TrajectoriesZh,
    quality: QualityZh,
    finance: FinanceZh,
    "finance-aid": FinanceAidZh,
  },
};

export const getStaticPaths: GetStaticPaths = async () => {
  const articles = ["access", "equity", "learning", "trajectories", "quality", "finance", "finance-aid"];

  const locales = ["en", "fr", "es", "ru", "ar", "zh", "de"];

  return {
    paths: locales.flatMap((locale) => articles.map((article) => ({ params: { article }, locale }))),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async () => {
  return { props: {} };
};

export default function Article() {
  const locale = useLocale();
  const { query } = useRouter();
  const article = query.article as string;
  const localeMap = localeContents[locale];
  const { default: Content, frontmatter } = localeMap[article];

  return (
    <DefaultMDXProvider frontmatter={frontmatter}>
      <Content />
    </DefaultMDXProvider>
  );
}
